import React, {
  ChangeEvent, ReactElement, useEffect, useRef,
} from 'react';
import { Button, LoaderButton } from 'idsk-react-components';
import { Tooltip } from 'slovlex-idsk-react-components';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import addIcon from '../../../../../assets/images/icon-plus.svg';
import addTooltipIcon from '../../../../../assets/images/icon-plus-tooltip.svg';
import editIcon from '../../../../../assets/images/icon-edit.svg';
import editTooltipIcon from '../../../../../assets/images/icon-edit-tooltip.svg';
import uploadIcon from '../../../../../assets/images/icon-upload.svg';
import uploadTooltipIcon from '../../../../../assets/images/icon-upload-tooltip.svg';
import downloadIcon from '../../../../../assets/images/icon-download-blue.svg';
import SprievodnyDokumentMergedItem from '../../../../model/pracovnyKrok/SprievodnyDokumentMergedItem';
import DownloadSprievodnyDokumentButton from '../../component/DownloadSprievodnyDokumentButton';
import DeleteSprievodnyDokumentButton from './DeleteSprievodnyDokumentButton';
import fileDownloadErrorStatus from '../../../../constants/FileDownloadErrorConstant';
import { ClientStatus } from '../../../../model/ClientStatus';
import Uuid from '../../../../model/Uuid';
import './Actions.scss';
import { useUserContext } from '../../../../context/UserContext';
import { UserRoleEnum } from '../../../../model/currentuser/UserRole';
import routes from '../../../../routing/routes';
import { ObalMaterialuEnum } from '../../../../constants/ObalMaterialuEnum';
import { useClient } from '../../../../context/ClientStatusContext';
import {
  createReportPripomienky,
  createReportVyhodnotenieMpk,
  createReportVyjadrenie,
} from '../../../../client/external/evidencnaaplikacia/sprievodnyDokumentReportClient';
import LegislativnyMaterialDetail from '../../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import useClientStatusContext from '../../../../context/ClientStatusContext/context/useClientStatusContext';
import StadiumMaterialuHistoria from '../../../../model/legislativnyMaterial/StadiumMaterialuHistoria';
import { useSuccessMessageContext } from '../../../../context/SuccessMessageContext';

function getTypObaluMaterialu(dokument: SprievodnyDokumentMergedItem): ObalMaterialuEnum {
  const formData = dokument?.formData;
  if (formData === null || formData === undefined || formData === '') {
    return undefined;
  }

  try {
    const schema = z.object({ obalMaterialuEnum: z.string() });
    const jsonData = schema.parse(JSON.parse(formData));

    switch (jsonData.obalMaterialuEnum) {
      case 'VSEOBECNY':
        return ObalMaterialuEnum.VSEOBECNY;
      case 'NR_SR':
        return ObalMaterialuEnum.NR_SR;
      default:
        return undefined;
    }
  } catch (ex) {
    return undefined;
  }
}

interface Props {
  setSuccessMessageToDisplay?: (message: string) => void;
  selectedStadium?: StadiumMaterialuHistoria,
  sprievodnyDokument?: SprievodnyDokumentMergedItem;
  uploadDokument: (e: ChangeEvent<HTMLInputElement>, document?: SprievodnyDokumentMergedItem) => void;
  uploadDokumentStatus?: ClientStatus;
  deleteDokument: (dokumentUuid: Uuid) => void;
  deleteDokumentStatus?: ClientStatus;
  downloadDokument: (dokumentUuid: Uuid, filename: string) => void;
  downloadDokumentStatus?: ClientStatus
  materialDetail?: LegislativnyMaterialDetail;
  reloadSprievodnyDokumentList?: () => void;
  useTooltipIcons?: boolean;
}

const Actions = ({
  setSuccessMessageToDisplay,
  selectedStadium,
  sprievodnyDokument,
  uploadDokument,
  uploadDokumentStatus,
  deleteDokument,
  deleteDokumentStatus,
  downloadDokument,
  downloadDokumentStatus,
  materialDetail,
  reloadSprievodnyDokumentList,
  useTooltipIcons,
}: Props): ReactElement => {
  const ref = useRef<HTMLInputElement>();
  const { pouzivatel } = useUserContext();
  const { handleStatus } = useClientStatusContext();

  useEffect(() => {
    if (useTooltipIcons && downloadDokumentStatus?.responseCode === 404) {
      handleStatus(fileDownloadErrorStatus);
    }
  }, [useTooltipIcons, downloadDokumentStatus, handleStatus]);

  const isSuperAdmin = pouzivatel.roles?.includes(UserRoleEnum.SUPERADMIN);
  const documentExists = sprievodnyDokument?.sprievodnyDokumentUuid;
  const isGenerovany = sprievodnyDokument?.typDokumentu?.generovanySystemom === true;
  const hasFormular = sprievodnyDokument?.typDokumentu?.existujeFormular
    && sprievodnyDokument?.typDokumentu?.povolitZadavanieFormularom;
  const navigate = useNavigate();
  const { clearMessages } = useSuccessMessageContext();
  const openFileSelectWindow = (): void => {
    ref.current.click();
  };

  const handleEdit = (): void => {
    if (sprievodnyDokument?.typDokumentu?.kod === 'ObalMaterialu') {
      const typObalu = getTypObaluMaterialu(sprievodnyDokument);
      if (typObalu !== undefined) {
        navigate(getRoute(false, typObalu, sprievodnyDokument.sprievodnyDokumentUuid));
      }
    } else {
      navigate(getRoute(false));
    }
  };

  const {
    wrapClient: createReportVyhodnotenieMpkClient,
    status: createReportVyhodnotenieMpkStatus,
  } = useClient(createReportVyhodnotenieMpk);

  const {
    wrapClient: createReportPripomienkyClient,
    status: createReportPripomienkyStatus,
  } = useClient(createReportPripomienky);

  const {
    wrapClient: createReportVyjadrenieClient,
    status: createReportVyjadrenieStatus,
  } = useClient(createReportVyjadrenie);

  const generateVyhodnotenieMpk = async (): Promise<void> => {
    const response = await createReportVyhodnotenieMpkClient(materialDetail?.uuid, selectedStadium.uuid);
    if (response.data !== null && response.data !== '') {
      const newStatus: ClientStatus = {
        responseCode: undefined,
        loading: false,
        finished: true,
        generalErrors: [{ message: response.data }],
        fieldErrors: [],
        failed: false,
      };
      handleStatus(newStatus);
    } else {
      setSuccessMessageToDisplay('Dokument bol úspešne vygenerovaný.');
      reloadSprievodnyDokumentList();
    }
  };

  const generateReport = async (): Promise<void> => {
    clearMessages();
    switch (sprievodnyDokument?.typDokumentu?.kod) {
      case 'VyjadreniaKPredbeznejInformacii':
        await createReportVyjadrenieClient(
          materialDetail?.uuid,
          selectedStadium.uuid,
        );
        setSuccessMessageToDisplay('Dokument bol úspešne vygenerovaný.');
        reloadSprievodnyDokumentList();
        break;
      case 'VyhodnotenieMedzirezortnehoPripomienkovehoKonania':
        await generateVyhodnotenieMpk();
        break;
      case 'VznesenePripomienkyVRamciMedzirezortnehoPripomienkovehoKonania':
        await createReportPripomienkyClient(materialDetail?.uuid, selectedStadium.uuid);
        setSuccessMessageToDisplay('Dokument bol úspešne vygenerovaný.');
        reloadSprievodnyDokumentList();
        break;
      default:
        reloadSprievodnyDokumentList();
        break;
    }
  };

  const {
    type,
    year,
    index,
    pracovnyKrokId,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, pracovnyKrokId: string }>();

  const getRoute = (isCreate: boolean, typObalu?: ObalMaterialuEnum, sprievodnyDokumentUuid?: string): string => {
    switch (sprievodnyDokument?.typDokumentu?.kod) {
      case 'NavrhUzneseniaVladySlovenskejRepubliky':
        return isCreate
          ? routes.procesy.detail.sprievodneDokumenty.navrhUzneseniaVlady.create
            .withParams(type, year, index, selectedStadium.uuid)
          : routes.procesy.detail.sprievodneDokumenty.navrhUzneseniaVlady.edit
            .withParams(type, year, index, selectedStadium.uuid);
      case 'SpravaOUcastiVerejnosti':
        return isCreate
          ? routes.procesy.detail.sprievodneDokumenty.spravaOUcastiVerejnosti.create
            .withParams(type, year, index, selectedStadium.uuid)
          : routes.procesy.detail.sprievodneDokumenty.spravaOUcastiVerejnosti.edit
            .withParams(type, year, index, selectedStadium.uuid);
      case 'DolozkaZlucitelnosti':
        return isCreate
          ? routes.procesy.detail.sprievodneDokumenty.dolozkaZlucitelnosti.create
            .withParams(type, year, index, selectedStadium.uuid)
          : routes.procesy.detail.sprievodneDokumenty.dolozkaZlucitelnosti.edit
            .withParams(type, year, index, selectedStadium.uuid);
      case 'DolozkaVplyvov':
        return isCreate
          ? routes.procesy.detail.sprievodneDokumenty.dolozkaVplyvov.create
            .withParams(type, year, index, selectedStadium.uuid)
          : routes.procesy.detail.sprievodneDokumenty.dolozkaVplyvov.edit
            .withParams(type, year, index, selectedStadium.uuid);
      case 'UznesenieVladySR':
        return isCreate
          ? routes.procesy.detail.sprievodneDokumenty.uznesenieVlady.create
            .withParams(type, year, index, selectedStadium.uuid)
          : routes.procesy.detail.sprievodneDokumenty.uznesenieVlady.edit
            .withParams(type, year, index, selectedStadium.uuid);
      case 'ObalMaterialu':
        if (isCreate) {
          return routes.procesy.detail.sprievodneDokumenty.obalMaterialu.select.withParams(
            type,
            year,
            index,
            selectedStadium.uuid,
            selectedStadium.stadium.hodnota,
          );
        }
        return typObalu === ObalMaterialuEnum.VSEOBECNY
          ? routes.procesy.detail.sprievodneDokumenty.obalMaterialu.vseobecny.edit.withParams(
            type,
            year,
            index,
            sprievodnyDokumentUuid,
            selectedStadium.uuid,
            selectedStadium.stadium.hodnota,
          )
          : routes.procesy.detail.sprievodneDokumenty.obalMaterialu.obalNrSr.edit
            .withParams(type, year, index, sprievodnyDokumentUuid, selectedStadium.uuid);
      case 'ObalMaterialuBezchybnost':
        return isCreate
          ? routes.procesy.podpisPredseda.obalMaterialu.create.withParams(type, year, index, pracovnyKrokId)
          : routes.procesy.podpisPredseda.obalMaterialu.edit.withParams(type, year, index, pracovnyKrokId);
      default:
        return undefined;
    }
  };

  const tooltipModifier = useTooltipIcons ? ' sprievodny-dokument-action-buttons--tooltip' : '';

  return (
    <div className={`sprievodny-dokument-action-buttons${tooltipModifier}`}>
      {useTooltipIcons ? (
        sprievodnyDokument?.sprievodnyDokumentUuid && sprievodnyDokument?.nazov && (
          <Tooltip tooltipText="Stiahnuť dokument" className="sprievodny-dokument-action-tooltip">
            <LoaderButton
              loading={downloadDokumentStatus?.loading}
              className="sprievodny-dokument-button download-sprievodny-dokument-button"
              customIconBefore={downloadIcon}
              size="small"
              buttonStyle="link"
              onClick={() => downloadDokument(sprievodnyDokument.sprievodnyDokumentUuid, sprievodnyDokument.nazov)}
              disableOutsideClickWhileLoading
            />
          </Tooltip>
        )
      ) : (
        <DownloadSprievodnyDokumentButton
          sprievodnyDokumentUuid={sprievodnyDokument?.sprievodnyDokumentUuid}
          filename={sprievodnyDokument?.nazov}
          downloadDokument={downloadDokument}
          downloadDokumentStatus={downloadDokumentStatus}
        />
      )}
      {documentExists && hasFormular && sprievodnyDokument.formData && (
        useTooltipIcons ? (
          <Tooltip tooltipText="Upraviť dokument" className="sprievodny-dokument-action-tooltip">
            <Button
              className="sprievodny-dokument-button"
              size="small"
              buttonStyle="link"
              iconBefore={editTooltipIcon}
              onClick={handleEdit}
            />
          </Tooltip>
        ) : (
          <Button
            className="sprievodny-dokument-button"
            size="small"
            buttonStyle="link"
            iconBefore={editIcon}
            onClick={handleEdit}
          >Upraviť
          </Button>
        )
      )}
      {documentExists && (!isGenerovany || isSuperAdmin) && (
        <DeleteSprievodnyDokumentButton
          sprievodnyDokumentUuid={sprievodnyDokument?.sprievodnyDokumentUuid}
          deleteDokument={deleteDokument}
          deleteDokumentStatus={deleteDokumentStatus}
          useTooltipIcon={useTooltipIcons}
        />
      )}
      {!documentExists && hasFormular && !isGenerovany && (
        useTooltipIcons ? (
          <Tooltip tooltipText="Vytvoriť dokument" className="sprievodny-dokument-action-tooltip">
            <Button
              className="sprievodny-dokument-button"
              size="small"
              buttonStyle="link"
              iconBefore={addTooltipIcon}
              onClick={() => navigate(getRoute(true))}
            />
          </Tooltip>
        ) : (
          <Button
            className="sprievodny-dokument-button"
            size="small"
            buttonStyle="link"
            iconBefore={addIcon}
            onClick={() => navigate(getRoute(true))}
          >Vytvoriť
          </Button>
        )
      )}
      {!documentExists && isGenerovany && isSuperAdmin && (
        useTooltipIcons ? (
          <Tooltip tooltipText="Vygenerovať dokument" className="sprievodny-dokument-action-tooltip">
            <LoaderButton
              className="sprievodny-dokument-button"
              size="small"
              buttonStyle="link"
              customIconBefore={addTooltipIcon}
              onClick={generateReport}
              loading={createReportPripomienkyStatus.loading
                || createReportVyjadrenieStatus.loading || createReportVyhodnotenieMpkStatus.loading}
              disableOutsideClickWhileLoading
            />
          </Tooltip>
        ) : (
          <LoaderButton
            className="sprievodny-dokument-button"
            size="small"
            buttonStyle="link"
            customIconBefore={addIcon}
            onClick={generateReport}
            loading={createReportPripomienkyStatus.loading
              || createReportVyjadrenieStatus.loading || createReportVyhodnotenieMpkStatus.loading}
            disableOutsideClickWhileLoading
          >Vygenerovať
          </LoaderButton>
        )
      )}
      {!documentExists && (!isGenerovany || isSuperAdmin) && (
        <>
          {useTooltipIcons ? (
            <Tooltip tooltipText="Nahrať dokument" className="sprievodny-dokument-action-tooltip">
              <LoaderButton
                loading={uploadDokumentStatus?.loading}
                className="sprievodny-dokument-button"
                size="small"
                buttonStyle="link"
                customIconBefore={uploadTooltipIcon}
                onClick={openFileSelectWindow}
                disableOutsideClickWhileLoading
              />
            </Tooltip>
          ) : (
            <LoaderButton
              loading={uploadDokumentStatus?.loading}
              className="sprievodny-dokument-button"
              size="small"
              buttonStyle="link"
              customIconBefore={uploadIcon}
              onClick={openFileSelectWindow}
              disableOutsideClickWhileLoading
            >Nahrať dokument
            </LoaderButton>
          )}
          <input
            onChange={event => uploadDokument(event, sprievodnyDokument)}
            hidden
            ref={ref}
            type="file"
          />
        </>
      )}
    </div>
  );
};
export default Actions;
